import type { Area } from 'react-easy-crop';

/** The image an app surface is given: its shape, and its width in pixels. */
export interface ImageFrame {
  /** Width over height. */
  aspect: number;
  /** A smaller crop is kept at its own size, never enlarged. */
  width: number;
}

/** A part of the frame an app surface shows on its own: centred, at its own shape. */
export interface ImageGuide {
  label: string;
  aspect: number;
}

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const;

export const isImageType = (type: string): boolean =>
  (IMAGE_TYPES as readonly string[]).includes(type);

/** How far the image may move so it keeps covering the frame, or stays inside it when smaller. */
export function clampOffset(offset: number, media: number, frame: number, zoom: number): number {
  const max = Math.abs((media * zoom) / 2 - frame / 2);
  return Math.min(max, Math.max(-max, offset));
}

/** The size the cropped image is written at. */
export function outputSize(area: Area, frame: ImageFrame): { width: number; height: number } {
  const width = Math.max(1, Math.min(frame.width, Math.round(area.width)));
  return { width, height: Math.max(1, Math.round(width / frame.aspect)) };
}

function load(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("The image couldn't be read."));
    image.src = src;
  });
}

function transparent(context: CanvasRenderingContext2D, width: number, height: number): boolean {
  const pixels = context.getImageData(0, 0, width, height).data;
  for (let i = 3; i < pixels.length; i += 4) if (pixels[i]! < 255) return true;
  return false;
}

/** `area` is in natural pixels, past the image when zoomed out. Only a see-through crop stays PNG. */
export async function cropToFile(
  src: string,
  area: Area,
  frame: ImageFrame,
  source: { name: string; type: string },
  background: string | null,
): Promise<File> {
  const image = await load(src);
  const { width, height } = outputSize(area, frame);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error("This browser can't crop images.");
  if (background) {
    context.fillStyle = background;
    context.fillRect(0, 0, width, height);
  }
  const scale = width / area.width;
  context.imageSmoothingQuality = 'high';
  context.drawImage(
    image,
    -area.x * scale,
    -area.y * scale,
    image.naturalWidth * scale,
    image.naturalHeight * scale,
  );
  const type =
    source.type !== 'image/jpeg' && transparent(context, width, height)
      ? 'image/png'
      : 'image/jpeg';
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.9));
  if (!blob) throw new Error("The cropped image couldn't be written.");
  const base = source.name.replace(/\.[^.]+$/, '') || 'image';
  return new File([blob], `${base}.${type === 'image/jpeg' ? 'jpg' : 'png'}`, { type: blob.type });
}
